// 发送消息接口常量（包含 SENDMESSAGE）以及当前站点 BASE_URL
import ApiEndpointConstants, {
  BASE_URL,
} from "../constans/ApiEndpointConstants";
// 封装好的 axios 实例
import api from "./API";
// 统一管理 localStorage 的工具类
import LocalStorageUtil from "../utils/localStorageUtil";
// 通过 getpixel 接口可以拿到 groupid 等埋点相关配置
import * as GetPixelAPI from "./GetPixelAPI";

// 用于缓存「正在进行中的 groupId 请求」，避免同一时间多次并发请求后台
let groupIdPromise = null;

// 简单去重缓存：短时间窗口内相同的 text 只发送一次，避免 StrictMode 下重复发送
const recentSends = new Map(); // text -> timestamp(ms)
const DEDUPE_MS = 10000; // 10 秒内的重复发送视为重复并跳过

// 确保拿到一个可用的 groupId：优先使用入参，其次本地缓存，最后从 getpixel 接口拉取
const ensureGroupId = async (incomingGroupId) => {
  // 1. 如果调用方主动传入了 groupid，就直接用它，并顺便写入本地缓存
  if (incomingGroupId) {
    LocalStorageUtil.saveGroupId(incomingGroupId);
    return incomingGroupId;
  }

  // 2. 如果本地已经有缓存的 groupid，直接返回缓存
  const cachedGroupId = LocalStorageUtil.getGroupId();
  if (cachedGroupId) {
    return cachedGroupId;
  }

  // 3. 缓存中也没有，则需要通过 getpixel 接口去拉取一次
  //    为了避免多次并发调用，这里用 groupIdPromise 做一个「进行中的单例请求」
  if (!groupIdPromise) {
    const strippedUrl = BASE_URL.replace(/^https?:\/\//, "");
    groupIdPromise = GetPixelAPI.getpixel(strippedUrl)
      .then((res) => {
        // 从接口结果中读取 groupid，没有则回退为空字符串
        const fetched = res?.data?.data?.groupid || "";
        // 拿到有效的 groupid 后写入本地缓存，后续复用
        if (fetched) {
          LocalStorageUtil.saveGroupId(fetched);
        }
        return fetched;
      })
      .catch((error) => {
        // 接口失败时打印错误日志，但不阻塞发送消息流程，统一回退为 ""
        console.error("Failed to fetch group id", error);
        return "";
      })
      .finally(() => {
        // 本轮请求结束后，清空 promise，方便下一次重新发起
        groupIdPromise = null;
      });
  }

  return groupIdPromise;
};

// 对外暴露的发送消息方法
// text：要发送的消息文本
// groupid：可选，如果不传则内部会自动根据上述逻辑获取 / 生成 groupId
export const sendMessage = async (text, groupid) => {
  // 如果 text 存在且在短时间窗口内已发送过，则直接跳过发送
  if (text) {
    const now = Date.now();
    const lastSent = recentSends.get(text) || 0;
    if (now - lastSent < DEDUPE_MS) {
      // skip duplicate
      return;
    }
    recentSends.set(text, now);
    // 清理过旧缓存项（避免 map 不断增长）
    for (const [k, ts] of recentSends) {
      if (now - ts > DEDUPE_MS * 5) {
        recentSends.delete(k);
      }
    }
  }

  const finalGroupId = await ensureGroupId(groupid);

  // 处理 text 中的域名，自动添加后缀
  let processedText = text;
  if (text && BASE_URL) {
    // 确保 BASE_URL 不以 / 结尾
    const baseUrlNormalized = BASE_URL.replace(/\/$/, "");

    if (text.includes(baseUrlNormalized)) {
      processedText = text;
    }
  }

  return api.post(`${ApiEndpointConstants.SENDMESSAGE}`, {
    text: processedText,
    groupid: finalGroupId,
  });
};
